// ===========================================
// Integration Key Manager Component
// ===========================================
// Manages per-school encrypted API keys for an integration
// Shows masked values, status chips, and action buttons.
// NEVER displays or handles actual decrypted key values on the client
// beyond the initial form submission.

import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  Delete as DeleteIcon,
  CheckCircle as VerifiedIcon,
  Error as FailedIcon,
  HelpOutline as UnverifiedIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import {
  useIntegrationKeyStatus,
  useSetIntegrationKey,
  useDeleteIntegrationKey,
  useVerifyIntegrationKey,
} from '../../../hooks/useIntegrations';
import { usePermissions } from '../../../hooks/usePermissions';
import type { IntegrationProvider, IntegrationKeyStatus } from '../../../types/integrations.types';

// ===========================================
// KEY DEFINITIONS (static config, matches backend)
// ===========================================

const INTEGRATION_KEY_DEFS: Record<IntegrationProvider, Array<{
  keyName: string;
  label: string;
  required: boolean;
  sensitive: boolean;
  placeholder?: string;
}>> = {
  stripe: [
    { keyName: 'secretKey', label: 'Secret Key', required: true, sensitive: true, placeholder: 'sk_live_...' },
    { keyName: 'publishableKey', label: 'Publishable Key', required: true, sensitive: false, placeholder: 'pk_live_...' },
    { keyName: 'webhookSecret', label: 'Webhook Secret', required: false, sensitive: true, placeholder: 'whsec_...' },
  ],
  twilio: [
    { keyName: 'accountSid', label: 'Account SID', required: true, sensitive: false, placeholder: 'AC...' },
    { keyName: 'authToken', label: 'Auth Token', required: true, sensitive: true },
    { keyName: 'smsFromNumber', label: 'SMS From Number', required: false, sensitive: false, placeholder: '+61...' },
  ],
  whatsapp: [
    { keyName: 'phoneNumberId', label: 'Phone Number ID', required: true, sensitive: false },
    { keyName: 'accessToken', label: 'Access Token', required: true, sensitive: true },
    { keyName: 'appSecret', label: 'App Secret', required: true, sensitive: true },
  ],
  mailgun: [
    { keyName: 'apiKey', label: 'API Key', required: true, sensitive: true },
    { keyName: 'domain', label: 'Domain', required: true, sensitive: false, placeholder: 'mg.example.com' },
    { keyName: 'fromEmail', label: 'From Email', required: false, sensitive: false, placeholder: 'noreply@example.com' },
  ],
  pixabay: [
    { keyName: 'apiKey', label: 'API Key', required: true, sensitive: true },
  ],
  slack: [
    { keyName: 'clientId', label: 'Client ID', required: true, sensitive: false, placeholder: '1234567890.1234567890' },
    { keyName: 'clientSecret', label: 'Client Secret', required: true, sensitive: true },
    { keyName: 'signingSecret', label: 'Signing Secret', required: true, sensitive: true },
  ],
};

const INTEGRATION_LABELS: Record<IntegrationProvider, string> = {
  stripe: 'Stripe',
  twilio: 'Twilio',
  whatsapp: 'WhatsApp',
  mailgun: 'Mailgun',
  pixabay: 'Pixabay',
  slack: 'Slack',
};

// ===========================================
// PROPS
// ===========================================

interface IntegrationKeyManagerProps {
  integration: IntegrationProvider;
}

// ===========================================
// COMPONENT
// ===========================================

/** Stored API keys are ADMIN only (roles plan 5.6): the gate lives here so no tab can forget it. */
export default function IntegrationKeyManager(props: IntegrationKeyManagerProps) {
  const { isOwner } = usePermissions();
  if (!isOwner) return null;
  return <IntegrationKeyManagerPanel {...props} />;
}

function IntegrationKeyManagerPanel({ integration }: IntegrationKeyManagerProps) {
  const { data: statuses, isLoading } = useIntegrationKeyStatus();
  const setKey = useSetIntegrationKey();
  const deleteKey = useDeleteIntegrationKey();
  const verifyKey = useVerifyIntegrationKey();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogKeyName, setDialogKeyName] = useState('');
  const [dialogValue, setDialogValue] = useState('');
  const [dialogVerify, setDialogVerify] = useState(true);
  const [showValue, setShowValue] = useState(false);
  const [confirmDeleteKey, setConfirmDeleteKey] = useState<string | null>(null);

  const integrationStatus = statuses?.find(
    (s: IntegrationKeyStatus) => s.integration === integration
  );
  const keyDefs = INTEGRATION_KEY_DEFS[integration] || [];

  const handleOpenDialog = (keyName: string) => {
    setDialogKeyName(keyName);
    setDialogValue('');
    setDialogVerify(true);
    setShowValue(false);
    setDialogOpen(true);
  };

  const handleSaveKey = async () => {
    if (!dialogValue.trim()) return;

    await setKey.mutateAsync({
      integration,
      keyName: dialogKeyName,
      value: dialogValue.trim(),
      verify: dialogVerify,
    });

    setDialogOpen(false);
    setDialogValue('');
  };

  const handleDeleteKey = async (keyName: string) => {
    await deleteKey.mutateAsync({ integration, keyName });
    setConfirmDeleteKey(null);
  };

  const handleVerifyKey = async (keyName: string) => {
    await verifyKey.mutateAsync({ integration, keyName });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const sourceLabel = integrationStatus?.source === 'school'
    ? 'Using school-specific keys'
    : integrationStatus?.source === 'system'
    ? 'Using system-level keys'
    : 'Not configured';

  const sourceColor = integrationStatus?.source === 'school'
    ? 'success'
    : integrationStatus?.source === 'system'
    ? 'info'
    : 'warning';

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        avatar={<KeyIcon />}
        title={`${INTEGRATION_LABELS[integration]} API Keys`}
        subheader={sourceLabel}
        action={
          <Chip
            label={integrationStatus?.source || 'none'}
            color={sourceColor as 'success' | 'info' | 'warning'}
            size="small"
            variant="outlined"
          />
        }
      />
      <CardContent>
        {integrationStatus?.source === 'system' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Currently using system-level keys from the server configuration.
            Set school-specific keys below to use your own account.
          </Alert>
        )}

        {integrationStatus?.source === 'none' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No API keys configured. Set the required keys below to enable this integration.
          </Alert>
        )}

        <Stack spacing={1}>
          {keyDefs.map((def) => {
            const storedKey = integrationStatus?.keys.find(
              (k) => k.keyName === def.keyName
            );
            const isSet = storedKey?.isSet ?? false;

            return (
              <Box
                key={def.keyName}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.default',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {def.label}
                    {def.required && (
                      <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                        *
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {isSet ? storedKey?.maskedHint || 'Set' : 'Not set'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Verification status */}
                  {isSet && storedKey && (
                    <Tooltip title={`Status: ${storedKey.verificationStatus}`}>
                      {storedKey.verificationStatus === 'verified' ? (
                        <VerifiedIcon color="success" fontSize="small" />
                      ) : storedKey.verificationStatus === 'failed' ? (
                        <FailedIcon color="error" fontSize="small" />
                      ) : (
                        <UnverifiedIcon color="action" fontSize="small" />
                      )}
                    </Tooltip>
                  )}

                  {/* Verify button */}
                  {isSet && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => handleVerifyKey(def.keyName)}
                      disabled={verifyKey.isPending}
                    >
                      Verify
                    </Button>
                  )}

                  {/* Set/Update button */}
                  <Button
                    size="small"
                    variant={isSet ? 'text' : 'contained'}
                    onClick={() => handleOpenDialog(def.keyName)}
                  >
                    {isSet ? 'Update' : 'Set'}
                  </Button>

                  {/* Delete button */}
                  {isSet && (
                    <Tooltip title="Remove school-specific key">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setConfirmDeleteKey(def.keyName)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>

        {/* Set Key Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {keyDefs.find((d) => d.keyName === dialogKeyName)?.label || dialogKeyName}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              type={showValue ? 'text' : 'password'}
              value={dialogValue}
              onChange={(e) => setDialogValue(e.target.value)}
              placeholder={keyDefs.find((d) => d.keyName === dialogKeyName)?.placeholder}
              margin="dense"
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowValue(!showValue)} edge="end" size="small">
                    {showValue ? <HideIcon /> : <ShowIcon />}
                  </IconButton>
                ),
              }}
            />
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant={dialogVerify ? 'contained' : 'outlined'}
                onClick={() => setDialogVerify(!dialogVerify)}
                sx={{ textTransform: 'none' }}
              >
                {dialogVerify ? 'Will verify after saving' : 'Save without verifying'}
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveKey}
              variant="contained"
              disabled={!dialogValue.trim() || setKey.isPending}
            >
              {setKey.isPending ? 'Saving...' : 'Save Key'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirm Delete Dialog */}
        <Dialog open={!!confirmDeleteKey} onClose={() => setConfirmDeleteKey(null)}>
          <DialogTitle>Delete Key</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the{' '}
              <strong>{keyDefs.find((d) => d.keyName === confirmDeleteKey)?.label}</strong> key?
              The integration will fall back to system-level keys if available.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteKey(null)}>Cancel</Button>
            <Button
              onClick={() => confirmDeleteKey && handleDeleteKey(confirmDeleteKey)}
              color="error"
              variant="contained"
              disabled={deleteKey.isPending}
            >
              {deleteKey.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
